import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

type EventTime = {
  startTime: string | Date;
  endTime?: string | Date | null;
  isAllDay?: boolean;
  isEndAllDay?: boolean;
};

const midnight = (value: string | Date, zone: string) => {
  const time = dayjs(value).tz(zone);
  return time.isValid() && time.hour() === 0 && time.minute() === 0
    && time.second() === 0 && time.millisecond() === 0;
};

export function isUpcomingEventTime(time: EventTime, zone: string, now = new Date()): boolean {
  if (!time?.startTime || !dayjs(time.startTime).isValid()) return false;
  const startAllDay = time.isAllDay === true
    || (time.isAllDay !== false && midnight(time.startTime, zone));
  const allDay = time.endTime
    ? time.isEndAllDay === true || (time.isEndAllDay !== false
        && startAllDay && midnight(time.endTime, zone))
    : startAllDay;
  const boundary = dayjs(time.endTime || time.startTime).tz(zone);
  return boundary.isValid() && (allDay ? boundary.endOf('day') : boundary).valueOf() >= now.getTime();
}

export function displayableEventTimes<T extends EventTime>(times: T[], zone: string, now = new Date()): T[] {
  const seen = new Set<string>();
  return (times || []).filter((time) => {
    if (!isUpcomingEventTime(time, zone, now)) return false;
    const allDay = time.isAllDay === true
      || (time.isAllDay !== false && midnight(time.startTime, zone));
    // Deduplicate date-only representations for display; leave the editor's source intact.
    if (!allDay) return true;
    const end = !time.endTime ? '' : time.isEndAllDay === true
      ? dayjs(time.endTime).tz(zone).format('YYYY-MM-DD') : String(dayjs(time.endTime).valueOf());
    const key = `${dayjs(time.startTime).tz(zone).format('YYYY-MM-DD')}|${end}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
